import { useState, useEffect } from 'react';
import { Shield, Users, ShoppingBag, Wrench, CreditCard, Star, CheckCircle, TrendingUp, Award, Lock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated Counter Component
export function AnimatedCounter({ end, duration = 2000, prefix = '', suffix = '' }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="tabular-nums">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}

// Trust Stats Hook
export function useTrustStats() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalServicesCompleted: 0,
    totalProductsSold: 0,
    totalTransactions: 0,
    averageRating: 0,
    totalReviews: 0,
    satisfactionRate: 0,
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/platform/trust-stats`);
        setStats({ ...response.data, loading: false });
      } catch (error) {
        setStats({
          totalUsers: 0, totalServicesCompleted: 0, totalProductsSold: 0,
          totalTransactions: 0, averageRating: 0, totalReviews: 0,
          satisfactionRate: 0, loading: false
        });
      }
    };

    fetchStats();
  }, []);

  return stats;
}

export function hasAnyStats(stats) {
  return stats.totalUsers > 0 || stats.totalServicesCompleted > 0 || stats.totalTransactions > 0 || stats.totalReviews > 0;
}

// Main Trust Banner Component - Shows at top of pages
export function TrustBanner({ variant = 'full' }) {
  const stats = useTrustStats();

  if (stats.loading || !hasAnyStats(stats)) return null;

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-6 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="font-semibold">
              <AnimatedCounter end={stats.totalUsers} suffix="+" /> Trusted Users
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            <span>
              <AnimatedCounter end={stats.totalServicesCompleted} /> Services Completed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
            <span>{stats.averageRating} Rating</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span>Stripe Secured</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold flex items-center justify-center gap-2">
            <Award className="w-5 h-5 text-yellow-400" />
            Trusted by Thousands Across Fort Wayne
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          <StatCard
            icon={<Users className="w-8 h-8" />}
            value={<AnimatedCounter end={stats.totalUsers} suffix="+" />}
            label="Happy Customers"
            color="text-blue-300"
          />
          <StatCard
            icon={<Wrench className="w-8 h-8" />}
            value={<AnimatedCounter end={stats.totalServicesCompleted} />}
            label="Services Completed"
            color="text-green-300"
          />
          <StatCard
            icon={<ShoppingBag className="w-8 h-8" />}
            value={<AnimatedCounter end={stats.totalProductsSold} />}
            label="Products Sold"
            color="text-purple-300"
          />
          <StatCard
            icon={<CreditCard className="w-8 h-8" />}
            value={<AnimatedCounter end={stats.totalTransactions} />}
            label="Secure Payments"
            color="text-yellow-300"
          />
        </div>
        <div className="mt-4 flex items-center justify-center gap-4 text-sm text-blue-200">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span>{stats.averageRating} Average Rating</span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span>{stats.satisfactionRate}% Satisfaction Rate</span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-1">
            <Lock className="w-4 h-4" />
            <span>Stripe Secured Payments</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, color }) {
  return (
    <div className="text-center p-4 rounded-xl bg-white/10 backdrop-blur-sm">
      <div className={`${color} mb-2 flex justify-center`}>{icon}</div>
      <div className="text-2xl md:text-3xl font-bold">{value}</div>
      <div className="text-sm text-blue-200">{label}</div>
    </div>
  );
}

// Trust Badges Row - For product/service pages
export function TrustBadges({ size = 'md' }) {
  const sizeClasses = {
    sm: 'text-xs gap-2',
    md: 'text-sm gap-3',
    lg: 'text-base gap-4'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <div className={`flex flex-wrap items-center justify-center ${sizeClasses[size]} text-gray-600`}>
      <div className="flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full">
        <Shield className={iconSizes[size]} />
        <span>Verified Sellers</span>
      </div>
      <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
        <Lock className={iconSizes[size]} />
        <span>Secure Checkout</span>
      </div>
      <div className="flex items-center gap-1 bg-purple-50 text-purple-700 px-3 py-1 rounded-full">
        <CreditCard className={iconSizes[size]} />
        <span>Stripe Protected</span>
      </div>
      <div className="flex items-center gap-1 bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full">
        <Star className={`${iconSizes[size]} fill-yellow-500`} />
        <span>Top Rated</span>
      </div>
    </div>
  );
}

// Live Activity Feed
export function LiveActivityFeed() {
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await axios.get(`${API}/platform/recent-activity`);
        setActivities(response.data.activities || []);
      } catch (error) {
        // Mock recent activities
        setActivities([
          { type: 'service', message: 'Jump start service completed in Fort Wayne', time: '2 min ago' },
          { type: 'purchase', message: 'Auto parts sold on marketplace', time: '5 min ago' },
          { type: 'service', message: 'Towing service requested', time: '8 min ago' },
          { type: 'review', message: 'New 5-star review received', time: '12 min ago' },
        ]);
      }
    };

    fetchActivities();
    const interval = setInterval(fetchActivities, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-green-600" />
        Live Activity
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
      </h4>
      <div className="space-y-2">
        {activities.map((activity, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
            <span className="text-gray-700 truncate">{activity.message}</span>
            <span className="text-gray-400 text-xs flex-shrink-0">{activity.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sold Badge for products
export function SoldBadge({ count }) {
  return (
    <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1 shadow-lg">
      <CheckCircle className="w-3 h-3" />
      SOLD
    </div>
  );
}

// Purchase Count Badge
export function PurchaseCountBadge({ count }) {
  if (count < 1) return null;
  
  return (
    <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
      <ShoppingBag className="w-3 h-3" />
      <span>{count} purchased</span>
    </div>
  );
}

// Hero Stats Section for Landing Page
export function HeroTrustStats() {
  const stats = useTrustStats();

  if (stats.loading || !hasAnyStats(stats)) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6">
      <div className="text-center">
        <div className="text-3xl md:text-4xl font-bold text-white">
          <AnimatedCounter end={stats.totalUsers} suffix="+" />
        </div>
        <div className="text-blue-200 text-sm">Trusted Users</div>
      </div>
      <div className="text-center">
        <div className="text-3xl md:text-4xl font-bold text-white">
          <AnimatedCounter end={stats.totalServicesCompleted} />
        </div>
        <div className="text-blue-200 text-sm">Jobs Completed</div>
      </div>
      <div className="text-center">
        <div className="text-3xl md:text-4xl font-bold text-white">
          {stats.averageRating}
          <Star className="w-6 h-6 inline-block ml-1 fill-yellow-400 text-yellow-400" />
        </div>
        <div className="text-blue-200 text-sm">Average Rating</div>
      </div>
      <div className="text-center">
        <div className="text-3xl md:text-4xl font-bold text-white">
          <AnimatedCounter end={stats.satisfactionRate} suffix="%" />
        </div>
        <div className="text-blue-200 text-sm">Satisfaction</div>
      </div>
    </div>
  );
}

// Stripe Security Badge
export function StripeSecurityBadge({ variant = 'default' }) {
  if (variant === 'large') {
    return (
      <div className="flex items-center justify-center gap-3 bg-gray-100 rounded-lg p-4 border border-gray-200">
        <Lock className="w-6 h-6 text-green-600" />
        <div>
          <div className="font-semibold text-gray-900">Secure Payments</div>
          <div className="text-sm text-gray-600">Protected by Stripe with 256-bit SSL encryption</div>
        </div>
        <svg className="h-8" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
          <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.8 10.8 0 0 1-4.56 1c-4.01 0-6.83-2.5-6.83-7.14 0-4.07 2.5-7.19 6.4-7.19 3.76 0 5.84 2.86 5.84 7.02 0 .5-.04 1.14-.04 1.39zm-5.75-5.69c-1.22 0-2.04.94-2.2 2.41h4.33c-.1-1.47-.91-2.41-2.13-2.41zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V6.25h3.57l.24 1.04c.63-.49 1.64-1.23 3.34-1.23 2.98 0 5.56 2.75 5.56 7.01 0 4.81-2.58 7.23-5.67 7.23zm-.88-10.62c-.86 0-1.44.26-1.88.63l.03 6.2c.44.38 1.02.63 1.85.63 1.39 0 2.34-1.53 2.34-3.71 0-2.14-.95-3.75-2.34-3.75zM28.24 5.57c-1.47 0-2.65-.34-2.65-1.84 0-1.26 1.18-1.96 2.65-1.96 1.46 0 2.64.7 2.64 1.96 0 1.5-1.17 1.84-2.64 1.84zm2.05 14.43h-4.12V6.35h4.12v13.65zM19.95 20.3c-2.06 0-3.74-.53-4.96-1.28l.84-3.16c1.26.75 3.02 1.28 4.18 1.28.91 0 1.3-.23 1.3-.72 0-.57-.89-.78-2.06-1.2-1.85-.63-4.1-1.78-4.1-4.38 0-2.93 2.27-4.78 5.63-4.78 1.68 0 3.08.36 4.24 1.01l-.84 3.08c-1.01-.55-2.37-.95-3.48-.95-.78 0-1.2.23-1.2.68 0 .55.73.76 1.85 1.15 1.94.66 4.35 1.62 4.35 4.46 0 2.95-2.13 4.81-5.75 4.81zM5.8 20.2c-3.36 0-5.8-2.55-5.8-6.9C0 8.6 2.6 6.06 5.96 6.06c1.51 0 2.78.37 3.77.95l-.87 3.3c-.79-.47-1.76-.86-2.82-.86-1.68 0-2.92 1.25-2.92 3.75 0 2.25 1.11 3.66 2.92 3.66 1.01 0 2-.34 2.86-.91l.83 3.24a8.32 8.32 0 0 1-3.93 1.01z" fill="#6772E5"/>
        </svg>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <Lock className="w-4 h-4 text-green-600" />
      <span>Secured by</span>
      <svg className="h-5" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
        <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.8 10.8 0 0 1-4.56 1c-4.01 0-6.83-2.5-6.83-7.14 0-4.07 2.5-7.19 6.4-7.19 3.76 0 5.84 2.86 5.84 7.02 0 .5-.04 1.14-.04 1.39zm-5.75-5.69c-1.22 0-2.04.94-2.2 2.41h4.33c-.1-1.47-.91-2.41-2.13-2.41zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V6.25h3.57l.24 1.04c.63-.49 1.64-1.23 3.34-1.23 2.98 0 5.56 2.75 5.56 7.01 0 4.81-2.58 7.23-5.67 7.23zm-.88-10.62c-.86 0-1.44.26-1.88.63l.03 6.2c.44.38 1.02.63 1.85.63 1.39 0 2.34-1.53 2.34-3.71 0-2.14-.95-3.75-2.34-3.75zM28.24 5.57c-1.47 0-2.65-.34-2.65-1.84 0-1.26 1.18-1.96 2.65-1.96 1.46 0 2.64.7 2.64 1.96 0 1.5-1.17 1.84-2.64 1.84zm2.05 14.43h-4.12V6.35h4.12v13.65zM19.95 20.3c-2.06 0-3.74-.53-4.96-1.28l.84-3.16c1.26.75 3.02 1.28 4.18 1.28.91 0 1.3-.23 1.3-.72 0-.57-.89-.78-2.06-1.2-1.85-.63-4.1-1.78-4.1-4.38 0-2.93 2.27-4.78 5.63-4.78 1.68 0 3.08.36 4.24 1.01l-.84 3.08c-1.01-.55-2.37-.95-3.48-.95-.78 0-1.2.23-1.2.68 0 .55.73.76 1.85 1.15 1.94.66 4.35 1.62 4.35 4.46 0 2.95-2.13 4.81-5.75 4.81zM5.8 20.2c-3.36 0-5.8-2.55-5.8-6.9C0 8.6 2.6 6.06 5.96 6.06c1.51 0 2.78.37 3.77.95l-.87 3.3c-.79-.47-1.76-.86-2.82-.86-1.68 0-2.92 1.25-2.92 3.75 0 2.25 1.11 3.66 2.92 3.66 1.01 0 2-.34 2.86-.91l.83 3.24a8.32 8.32 0 0 1-3.93 1.01z" fill="#6772E5"/>
      </svg>
    </div>
  );
}

// Marketplace Stats Bar
export function MarketplaceStatsBar() {
  const stats = useTrustStats();

  return (
    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-xl mb-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            <span className="font-bold"><AnimatedCounter end={stats.totalUsers} /></span>
            <span className="text-purple-200 text-sm">Active Buyers</span>
          </div>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            <span className="font-bold"><AnimatedCounter end={stats.totalProductsSold} /></span>
            <span className="text-purple-200 text-sm">Products Sold</span>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" />
            <span className="font-bold">{stats.averageRating}</span>
            <span className="text-purple-200 text-sm">Avg Rating</span>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm">
          <Lock className="w-4 h-4" />
          <span>Stripe Secured</span>
        </div>
      </div>
    </div>
  );
}

export default TrustBanner;
